import { useQuery } from '@tanstack/react-query'

import type { Importacion, Informe, Mapeo } from './importacion'
import { consulta, ErrorDeLaApi, fetchConSesion, mensajePorEstado, pedir } from './nucleo'
import { reducirSiHaceFalta } from './reducir'
import type { Alta, EstimacionDelAlta, TipoDeWeb } from './tipos'

/**
 * El asistente de alta habla con el núcleo por cuatro llamadas
 * (docs/alta-de-web-con-plantillas.md §7, §8): crear la web, continuar con las
 * imágenes ya subidas, leer el progreso y reintentar. Todo lo que decide —qué plan
 * admite qué, qué plantilla vale para qué tipo, cuántas webs caben— lo decide el
 * núcleo y se enseña con sus palabras.
 */
export type Plantilla = {
  slug: string
  nombre: string
  tipo: TipoDeWeb
  descripcion: string
  etiquetas: string[]
  orden: number
  paginas: Array<{ clave: string; titulo: string; obligatoria: boolean; legal: null | string }>
  capturas: string[]
  /**
   * Las claves del brief que la receta va a pedir: es lo que el asistente enseña en «Materiales».
   * `rotulo` es cómo se le enseña al cliente («Título de testimonios»): lo escribe el núcleo, y
   * el panel no lleva su copia de la regla.
   */
  claves: Array<{ clave: string; rotulo: string; tipo: 'imagen' | 'imagenes' | 'lista' | 'relacion' | 'texto'; paginas: string[] }>
}

export const usePlantillas = (tipo: TipoDeWeb | null) =>
  useQuery({
    queryKey: ['plantillas', tipo],
    enabled: tipo !== null,
    queryFn: async () => (await pedir<{ plantillas: Plantilla[] }>(`/panel/plantillas${consulta({ tipo: tipo ?? undefined })}`)).plantillas,
  })

export type PeticionDeAlta = {
  cliente: number
  tipo: TipoDeWeb
  plantilla: string
  nombre: string
  slug: string
  dominio?: string
  marca: { primario?: string; secundario?: string; acento?: string; tipografia: 'clasica' | 'moderna' | 'sistema' }
  /** `horario`: texto libre («Lun a vie 9:00–19:00 · Sáb 9:00–14:00»), a `ajustes.horario`. */
  contacto: { telefono?: string; correo?: string; direccion?: string; horario?: string }
  /** Las variables de los textos legales (§6.1). Opcionales: lo que falte queda por rellenar en la legal. */
  fiscal?: { razonSocial?: string; identificacion?: string; direccion?: string }
  tienda?: { modo: 'catalogo' | 'tienda'; consulta: 'ambos' | 'formulario' | 'whatsapp'; moneda: string }
  brief: string
  paginas: string[]
  servicios: string[]
}

/**
 * Las imágenes ya subidas: por hueco, y las `sueltas` («Más imágenes»), que el núcleo coloca
 * en los huecos de la plantilla que se hayan quedado sin foto.
 */
export type MediosDelAlta = { logo?: number; logoEnOscuro?: number; favicon?: number; imagenes?: Record<string, number[]>; sueltas?: number[] }

export type ProgresoDeAlta = {
  sitio: number
  nombre: string
  alta: Alta | null
  version: null | { numero: number; estado: string }
  /** Lo que la IA de contenido no pudo escribir sin inventar: qué le toca contar al cliente. El `rotulo` lo pone el núcleo. */
  faltan: Array<{ clave: string; rotulo: string; nota: string }>
  /**
   * Los productos del Excel (solo en una tienda, y solo a quien puede importar): el fichero y,
   * cuando el alta llega a su paso, cómo va su importación (sin el informe entero: ese se pide
   * con su id, como en Productos → Importar).
   */
  productos?: null | { archivo: null | string; filas: null | number; importacion: null | (Omit<Importacion, 'informe'> & { totales: Informe['totales'] | null }) }
  /** Lleva un buen rato sin dar señales (el núcleo se reinició a mitad): se puede reintentar. */
  atascada?: boolean
}

/** Sube un documento del brief (PDF, Word o texto) a la web recién creada: el núcleo extrae el texto y guarda el original en privado. */
export const subirDocumentoDelAlta = async (sitio: number, fichero: File): Promise<{ nombre: string; caracteres: number }[]> => {
  const cuerpo = new FormData()
  cuerpo.append('sitio', String(sitio))
  cuerpo.append('archivo', fichero, fichero.name)
  const respuesta = await fetchConSesion('/api/panel/alta/brief', { method: 'POST', credentials: 'same-origin', body: cuerpo })
  const json = (await respuesta.json().catch(() => ({}))) as { documentos?: Array<{ nombre: string; caracteres: number }>; errors?: Array<{ message?: string }> }
  if (!respuesta.ok || !json.documentos) throw new Error(json.errors?.[0]?.message ?? `No se ha podido leer «${fichero.name}». ${mensajePorEstado(respuesta.status)}`)
  return json.documentos
}

/**
 * El Excel de productos con el mapeo confirmado, a la web recién creada y antes de continuar:
 * el núcleo lo lee, lo comprueba y lo guarda, y el trabajo del alta importa los productos en su
 * paso, antes de los menús y del diseño.
 */
export const subirProductosDelAlta = async (sitio: number, archivo: File, mapeo: Mapeo[]): Promise<{ archivo: string; filas: number }> => {
  const cuerpo = new FormData()
  cuerpo.append('sitio', String(sitio))
  cuerpo.append('mapeo', JSON.stringify(mapeo.map(({ indice, columna, campo }) => ({ indice, columna, campo }))))
  cuerpo.append('archivo', archivo, archivo.name)
  const respuesta = await fetchConSesion('/api/panel/alta/productos', { method: 'POST', credentials: 'same-origin', body: cuerpo })
  const json = (await respuesta.json().catch(() => ({}))) as { productos?: { archivo: string; filas: number }; errors?: Array<{ message?: string; data?: Record<string, unknown> }> }
  if (!respuesta.ok || !json.productos) {
    const primero = json.errors?.[0]
    throw new ErrorDeLaApi(primero?.message ?? `No se ha podido preparar «${archivo.name}». ${mensajePorEstado(respuesta.status)}`, respuesta.status, primero?.data ?? {})
  }
  return json.productos
}

export const alta = {
  iniciar: (peticion: PeticionDeAlta) => pedir<{ sitio: number; alta: Alta }>('/panel/alta', { metodo: 'POST', cuerpo: peticion }),
  /** Con `sinIA`, la web se monta sin llamar a ningún modelo: textos por rellenar y el diseño de la plantilla. Sin saldo para la IA, sin `sinIA` es un 402. */
  continuar: (sitio: number, medios: MediosDelAlta, opciones: { sinIA?: boolean } = {}) =>
    pedir<{ sitio: number; alta: Alta }>('/panel/alta/continuar', { metodo: 'POST', cuerpo: { sitio, medios, ...(opciones.sinIA ? { sinIA: true } : {}) } }),
  /** Lo que costaría la IA del alta, paso a paso, y si llega el saldo: se enseña antes de continuar. */
  estimacion: (sitio: number) => pedir<EstimacionDelAlta>(`/panel/alta/estimacion${consulta({ sitio })}`),
  reintentar: (sitio: number) => pedir<{ sitio: number; alta: Alta }>('/panel/alta/reintentar', { metodo: 'POST', cuerpo: { sitio } }),
}

/** La estimación del alta, solo mientras espera los materiales (después ya no hay nada que decidir). */
export const useEstimacionDelAlta = (sitio: number, activa: boolean) =>
  useQuery({ queryKey: ['alta', sitio, 'estimacion'], queryFn: () => alta.estimacion(sitio), enabled: activa })

/** El progreso, sondeado mientras el alta o la construcción siguen en marcha. */
export const useProgresoDeAlta = (sitio: number) =>
  useQuery({
    queryKey: ['alta', sitio],
    queryFn: () => pedir<ProgresoDeAlta>(`/panel/alta${consulta({ sitio })}`),
    refetchInterval: (consulta) => {
      const datos = consulta.state.data
      const enMarcha = !datos || !datos.alta || datos.alta.estado === 'en-cola' || datos.alta.estado === 'en-curso' || datos.alta.estado === 'materiales'
      const construyendo = datos?.version?.estado === 'en-cola' || datos?.version?.estado === 'construyendo'
      return enMarcha || construyendo ? 2500 : false
    },
  })

/**
 * Sube una imagen a la web recién creada: la misma puerta que Medios, con su cuota y su
 * `alt` (§8). Si pasa del máximo por fichero del plan, antes se reduce en el navegador
 * (api/reducir.ts), y `alReducir` avisa para que la pantalla lo diga mientras tanto.
 */
export const subirImagenDelAlta = async (
  sitio: number,
  espacio: number,
  original: File,
  alt: string,
  opciones: { alReducir?: (nombre: string) => void; maximoBytes?: null | number } = {},
): Promise<number> => {
  const fichero = await reducirSiHaceFalta(original, opciones.maximoBytes, undefined, () => opciones.alReducir?.(original.name))
  const cuerpo = new FormData()
  cuerpo.append('file', fichero, fichero.name)
  cuerpo.append('_payload', JSON.stringify({ alt, sitio, cliente: espacio }))
  const respuesta = await fetchConSesion('/api/medios', { method: 'POST', credentials: 'same-origin', body: cuerpo })
  const json = (await respuesta.json().catch(() => ({}))) as { doc?: { id: number }; errors?: Array<{ message?: string; data?: { errors?: Array<{ message?: string }> } }> }
  if (!respuesta.ok || !json.doc) {
    const primero = json.errors?.[0]
    throw new Error(primero?.data?.errors?.[0]?.message ?? primero?.message ?? `No se ha podido subir «${original.name}». ${mensajePorEstado(respuesta.status)}`)
  }
  return json.doc.id
}
